import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { generateInvoicePDF } from '../../../utils/invoiceGenerator';

const { 
  FiArrowLeft, FiPlus, FiSearch, FiFilter, FiDownload, FiCheck, 
  FiRefreshCw, FiFileText, FiDollarSign, FiAlertCircle, FiX, 
  FiChevronRight, FiCalendar, FiBriefcase, FiTag, FiCreditCard, FiPrinter,
  FiClock
} = FiIcons;

const InvoiceManager = () => {
  const navigate = useNavigate();
  const { 
    invoices = [], 
    companies = [], 
    updateInvoiceStatus, 
    createInvoice, 
    INVOICE_SENDER 
  } = useData();

  // --- STATE ---
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // --- MAPPING & ENRICHMENT ---
  const enrichedInvoices = useMemo(() => {
    return (invoices || []).map(inv => {
      const company = companies.find(c => c.id === inv.company_id || c.id === inv.companyId);
      return {
        ...inv,
        company,
        companyName: company?.name || 'Ukjent Bedrift',
        orgNumber: company?.org_number || company?.orgNumber || '-',
        sellerId: company?.salesAgentId || company?.raw_data?.salesAgentId || 'Direkte',
        subscription: company?.subscription || 'Standard',
        paymentMethod: company?.paymentMethod || company?.raw_data?.paymentMethod || 'invoice',
        promoCode: company?.appliedPromoCode || company?.raw_data?.appliedPromoCode || 'Ingen',
        display_num: inv.invoice_number || inv.invoiceNumber || `INV-${inv.id?.substring(0,5)}`,
        display_amount: inv.total_amount || inv.totalAmount || 0,
        display_date: inv.date || inv.created_at || new Date().toISOString(),
        display_due: inv.due_date || inv.dueDate || new Date().toISOString()
      };
    });
  }, [invoices, companies]);

  // --- FILTRERING ---
  const filteredInvoices = useMemo(() => {
    return enrichedInvoices.filter(inv => {
      const matchesSearch = inv.companyName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           inv.display_num.toString().includes(searchTerm);
      const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
      const matchesAgent = agentFilter === 'all' || inv.sellerId === agentFilter;
      const matchesPlan = planFilter === 'all' || inv.subscription === planFilter;
      const matchesPayment = paymentFilter === 'all' || inv.paymentMethod === paymentFilter;
      
      return matchesSearch && matchesStatus && matchesAgent && matchesPlan && matchesPayment;
    });
  }, [enrichedInvoices, searchTerm, statusFilter, agentFilter, planFilter, paymentFilter]);

  // --- STATISTIKK ---
  const stats = useMemo(() => {
    const total = filteredInvoices.reduce((sum, inv) => sum + inv.display_amount, 0);
    const paid = filteredInvoices.filter(i => i.status === 'paid').reduce((sum, inv) => sum + inv.display_amount, 0);
    const overdue = filteredInvoices.filter(i => i.status === 'overdue').reduce((sum, inv) => sum + inv.display_amount, 0);
    const pending = total - paid;
    return { total, paid, overdue, pending };
  }, [filteredInvoices]);

  // --- HANDLINGER ---
  const handleRunInvoicing = async () => {
    if (!window.confirm("Vil du generere fakturagrunnlag for alle aktive kunder for denne perioden?")) return;
    setIsGenerating(true);
    
    let count = 0;
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    for (const company of companies.filter(c => c.active)) {
      // Sjekk om faktura allerede finnes for denne måneden
      const existing = invoices.find(inv => {
        const d = new Date(inv.date || inv.created_at);
        return (inv.company_id === company.id || inv.companyId === company.id) && 
               d.getMonth() === currentMonth && 
               d.getFullYear() === currentYear;
      });

      if (!existing) {
        const items = [{
          description: `Abonnement: ${company.subscription || 'Standard'} - ${new Date().toLocaleString('nb-NO', { month: 'long', year: 'numeric' })}`,
          quantity: company.departments_count || 1,
          price: company.monthly_fee || company.monthlyFee || 990,
          vatRate: 25
        }];
        await createInvoice(company, items);
        count++;
      }
    }
    
    setIsGenerating(false);
    alert(`Fakturering fullført! ${count} nye fakturaer ble opprettet.`);
  };

  const handleExportCSV = () => {
    const headers = ["Fakturanr", "Dato", "Forfall", "Kunde", "OrgNr", "Abonnement", "Rabattkode", "Selger", "Betalingsmetode", "Beløp", "Status"];
    const csvContent = [
      headers.join(","),
      ...filteredInvoices.map(inv => [
        inv.display_num,
        new Date(inv.display_date).toLocaleDateString('nb-NO'),
        new Date(inv.display_due).toLocaleDateString('nb-NO'),
        `"${inv.companyName}"`,
        inv.orgNumber,
        inv.subscription,
        inv.promoCode,
        inv.sellerId,
        inv.paymentMethod,
        inv.display_amount,
        inv.status
      ].join(","))
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `fakturaeksport_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-[10px] font-black uppercase">Betalt</span>;
      case 'overdue': return <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-[10px] font-black uppercase">Forfalt</span>;
      case 'unpaid': return <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-[10px] font-black uppercase">Ubetalt</span>;
      case 'cancelled': return <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-[10px] font-black uppercase">Kansellert</span>;
      default: return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-[10px] font-black uppercase">{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] font-sans">
      <header className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/superadmin')} className="p-2.5 hover:bg-blue-50 rounded-xl transition-all text-blue-900 border border-transparent hover:border-blue-100">
              <SafeIcon icon={FiArrowLeft} size={20} />
            </button>
            <div>
              <h1 className="text-xl font-black text-blue-950 uppercase tracking-tight">Faktura og Regnskap</h1>
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Administrasjon & Oppfølging</p>
            </div>
          </div>
          <div className="flex gap-3">
             <button onClick={handleExportCSV} className="bg-white border border-blue-200 text-blue-700 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-blue-50 transition-all flex items-center gap-2 shadow-sm">
              <SafeIcon icon={FiDownload} /> Eksport CSV
            </button>
            <button onClick={handleRunInvoicing} disabled={isGenerating} className="bg-blue-800 text-white px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-900/20 disabled:opacity-50">
              {isGenerating ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : <SafeIcon icon={FiPlus} />}
              Kjør Fakturering
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-8 space-y-8">
        {/* KPI STATS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { label: 'Totalt Fakturert', value: stats.total, icon: FiFileText, color: 'blue' },
            { label: 'Betalt', value: stats.paid, icon: FiCheck, color: 'green' },
            { label: 'Utestående', value: stats.pending, icon: FiClock, color: 'amber' },
            { label: 'Forfalt', value: stats.overdue, icon: FiAlertCircle, color: 'red' }
          ].map((s, i) => (
            <div key={i} className="bg-white p-6 rounded-3xl border border-blue-50 shadow-sm flex items-center gap-5">
              <div className={`w-14 h-14 bg-${s.color}-50 text-${s.color}-600 rounded-2xl flex items-center justify-center`}>
                <SafeIcon icon={s.icon} size={24} />
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{s.label}</p>
                <p className="text-2xl font-black text-blue-950">{s.value.toLocaleString()} kr</p>
              </div>
            </div>
          ))}
        </div>

        {/* FILTERS */}
        <div className="bg-white p-6 rounded-[32px] border border-blue-50 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300" />
              <input 
                type="text" 
                placeholder="Søk i kunde eller fakturanr..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-blue-50 border-none rounded-2xl text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-blue-50 border-none rounded-xl px-4 py-3 text-xs font-bold uppercase text-blue-900 outline-none focus:ring-2 focus:ring-blue-500">
                <option value="all">Alle Statuser</option>
                <option value="paid">Betalt</option>
                <option value="unpaid">Ubetalt</option>
                <option value="overdue">Forfalt</option>
                <option value="cancelled">Kansellert</option>
              </select>
              <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="bg-blue-50 border-none rounded-xl px-4 py-3 text-xs font-bold uppercase text-blue-900 outline-none focus:ring-2 focus:ring-blue-500">
                <option value="all">Metode</option>
                <option value="invoice">Faktura</option>
                <option value="card">Kort / Stripe</option>
              </select>
              <button 
                onClick={() => {
                  setSearchTerm(''); setStatusFilter('all'); setAgentFilter('all'); 
                  setPlanFilter('all'); setPaymentFilter('all');
                }}
                className="p-3 text-blue-400 hover:text-blue-600 transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} />
              </button>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-[32px] border border-blue-50 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-blue-50/50 border-b border-blue-50">
                <tr>
                  <th className="px-8 py-5 text-[10px] font-black text-blue-400 uppercase tracking-widest">Nr / Dato</th>
                  <th className="px-8 py-5 text-[10px] font-black text-blue-400 uppercase tracking-widest">Kunde</th>
                  <th className="px-8 py-5 text-[10px] font-black text-blue-400 uppercase tracking-widest">Abonnement</th>
                  <th className="px-8 py-5 text-[10px] font-black text-blue-400 uppercase tracking-widest text-right">Beløp</th>
                  <th className="px-8 py-5 text-[10px] font-black text-blue-400 uppercase tracking-widest text-center">Status</th>
                  <th className="px-8 py-5 text-[10px] font-black text-blue-400 uppercase tracking-widest text-right">Handling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-50">
                {filteredInvoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-blue-50/30 transition-colors group cursor-pointer" onClick={() => setSelectedInvoice(inv)}>
                    <td className="px-8 py-6">
                      <p className="font-mono font-bold text-blue-900 text-sm">{inv.display_num}</p>
                      <p className="text-[10px] text-gray-400 font-bold">{new Date(inv.display_date).toLocaleDateString('nb-NO')}</p>
                    </td>
                    <td className="px-8 py-6">
                      <p className="font-bold text-blue-950">{inv.companyName}</p>
                      <p className="text-[10px] text-gray-400 font-medium">Org: {inv.orgNumber}</p>
                    </td>
                    <td className="px-8 py-6">
                      <p className="text-xs font-bold text-gray-700 capitalize">{inv.subscription}</p>
                      <p className="text-[10px] text-indigo-500 font-bold uppercase tracking-tighter">Selger: {inv.sellerId}</p>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <p className="font-black text-blue-900">{inv.display_amount.toLocaleString()} kr</p>
                      <p className="text-[9px] text-gray-400 uppercase">Inkl. mva</p>
                    </td>
                    <td className="px-8 py-6 text-center">
                      {getStatusBadge(inv.status)}
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={(e) => { e.stopPropagation(); generateInvoicePDF(inv, inv.company, INVOICE_SENDER); }}
                          className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                        >
                          <SafeIcon icon={FiDownload} />
                        </button>
                        <button className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                          <SafeIcon icon={FiChevronRight} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredInvoices.length === 0 && (
            <div className="p-20 text-center">
              <SafeIcon icon={FiFileText} size={48} className="mx-auto text-blue-100 mb-4" />
              <h3 className="text-lg font-bold text-blue-900">Ingen fakturaer funnet</h3>
              <p className="text-gray-400 text-sm">Prøv å endre på filtrene eller søket ditt.</p>
            </div>
          )}
        </div>
      </main>

      {/* DETAIL MODAL */}
      <AnimatePresence>
        {selectedInvoice && (
          <div className="fixed inset-0 bg-[#0F172A]/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-3xl rounded-[40px] shadow-2xl overflow-hidden border border-white/20"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-blue-900 p-10 text-white flex justify-between items-start">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-white/10 rounded-[28px] flex items-center justify-center backdrop-blur-md border border-white/10 shadow-xl">
                    <SafeIcon icon={FiFileText} size={36} className="text-blue-300" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black uppercase tracking-tighter">Faktura {selectedInvoice.display_num}</h2>
                    <div className="flex gap-4 mt-2">
                       <span className="text-blue-300 text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon={FiCalendar} /> {new Date(selectedInvoice.display_date).toLocaleDateString('nb-NO')}
                      </span>
                      <span className={`text-xs font-black uppercase tracking-widest px-2 py-0.5 rounded ${selectedInvoice.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {selectedInvoice.status}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedInvoice(null)} className="p-3 hover:bg-white/10 rounded-2xl transition-all">
                  <SafeIcon icon={FiX} size={28} />
                </button>
              </div>

              <div className="p-10 grid md:grid-cols-2 gap-10 overflow-y-auto max-h-[60vh]">
                <div className="space-y-8">
                  <section>
                    <h3 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] border-b border-blue-50 pb-3 mb-6">Kundeinformasjon</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Navn</p>
                        <p className="font-black text-blue-950 text-lg leading-tight">{selectedInvoice.companyName}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Org.nr</p>
                          <p className="font-bold text-gray-700">{selectedInvoice.orgNumber}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Metode</p>
                          <p className="font-bold text-gray-700 capitalize">{selectedInvoice.paymentMethod}</p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="space-y-8">
                  <section>
                    <h3 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] border-b border-blue-50 pb-3 mb-6">Administrasjon</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2 ml-1">Oppdater Status</label>
                        <div className="grid grid-cols-2 gap-2">
                          {['unpaid', 'paid', 'overdue', 'cancelled'].map(stat => (
                            <button 
                              key={stat}
                              onClick={() => { updateInvoiceStatus(selectedInvoice.id, stat); setSelectedInvoice({...selectedInvoice, status: stat}); }}
                              className={`py-2 rounded-xl text-[10px] font-black uppercase border transition-all ${selectedInvoice.status === stat ? 'bg-blue-900 text-white border-blue-900 shadow-md' : 'bg-white text-gray-500 border-gray-100 hover:bg-blue-50'}`}
                            >
                              {stat}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="md:col-span-2">
                   <h3 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] border-b border-blue-50 pb-3 mb-4">Linjer og Beløp</h3>
                   <div className="bg-blue-50/50 p-6 rounded-[24px] space-y-3">
                      {selectedInvoice.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="font-medium text-gray-700">{item.description} (x{item.quantity})</span>
                          <span className="font-bold text-blue-950">{item.price.toLocaleString()} kr</span>
                        </div>
                      ))}
                      <div className="pt-3 mt-3 border-t border-blue-100 flex justify-between items-center">
                        <span className="font-black text-blue-900 uppercase text-lg">Total å betale</span>
                        <span className="text-2xl font-black text-blue-900">{selectedInvoice.display_amount.toLocaleString()} kr</span>
                      </div>
                   </div>
                </div>
              </div>

              <div className="p-10 bg-blue-50 border-t border-blue-100 flex flex-col sm:flex-row justify-between gap-4">
                <button 
                  onClick={() => generateInvoicePDF(selectedInvoice, selectedInvoice.company, INVOICE_SENDER)}
                  className="px-8 py-4 bg-white border-2 border-blue-900 text-blue-900 rounded-[20px] font-black uppercase tracking-widest shadow-sm hover:bg-blue-900 hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPrinter} /> Print / Last ned PDF
                </button>
                <button onClick={() => setSelectedInvoice(null)} className="px-10 py-4 bg-blue-900 text-white rounded-[20px] font-black uppercase tracking-widest shadow-xl hover:bg-blue-950 transition-all">
                  Lukk
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoiceManager;